"use client";

import React from "react";
import Link from "next/link";
import { useRouter } from "@/i18n/navigation";
import { IoHomeOutline, IoChevronForwardOutline } from "react-icons/io5";
import styles from "./breadcrumbs.module.css";

// Ensure imports match your structure
import { Breadcrumb } from "@/types/types";
import { PurposeEnum, SortingEnum, TypeEnum } from "@/types/types";
import { buildLocationSearch } from "@/lib/buildSearchParams/buildSearchParams";
import { makeLocalizedHrefNoLocale } from "@/lib/localizedRoutes/routeHelpers";
import { LuHouse } from "react-icons/lu";

export default function Breadcrumbs({
  locale,
  breadcrumbs,
  theme = "light",
  targetAgencyId,
  targetUserId,
}: {
  locale: string;
  breadcrumbs: Breadcrumb[];
  theme?: "light" | "dark";
  targetAgencyId?: string;
  targetUserId?: string;
}) {
  const router = useRouter();

  if (!breadcrumbs || breadcrumbs.length === 0) return null;

  const handleClick = (crumb: Breadcrumb) => {
    // If it's a listing (no geo data/url), do nothing
    if (!crumb.url || crumb.type === "listing") return;
    console.log(crumb);
    if (crumb.type === "agency") {
      router.push(
        makeLocalizedHrefNoLocale(locale, "agency-profile", [crumb.url]),
      );
      return;
    }
    if (crumb.type === "user") {
      router.push(makeLocalizedHrefNoLocale(locale, "profiles", [crumb.url]));
      return;
    }

    const query = buildLocationSearch(
      {
        sorting: SortingEnum.TOPLISTINGS,
        bouding_box: {
          top_left_lat: crumb.map_view.north,
          top_left_lon: crumb.map_view.west,
          bottom_right_lat: crumb.map_view.south,
          bottom_right_lon: crumb.map_view.east,
          zoom: 11,
        },
        center: {
          lat: crumb.geo_location.lat,
          lon: crumb.geo_location.lon,
        },
      },
      crumb.name,
      crumb.listing_count,
    );
    console.log(
      makeLocalizedHrefNoLocale(locale, "search", [
        crumb.url,
        TypeEnum.APARTMENT_HOUSE,
        PurposeEnum.FORSALE,
        "1",
      ]),
    );

    if (targetAgencyId) query.target_agency_id = targetAgencyId;
    if (targetUserId) query.target_user_id = targetUserId;
    router.push({
      pathname: makeLocalizedHrefNoLocale(locale, "search", [
        crumb.url,
        TypeEnum.APARTMENT_HOUSE,
        PurposeEnum.FORSALE,
        "1",
      ]),
      query: query,
    });
  };

  return (
    <nav
      aria-label="Breadcrumb"
      className={`${styles.breadcrumbsNav} ${styles[theme]}`}
    >
      <ol className={styles.breadcrumbsList}>
        <li className={styles.breadcrumbItem}>
          <Link href={"/"} className={styles.breadcrumbLink}>
            <LuHouse className={styles.homeIcon} />
          </Link>
          <IoChevronForwardOutline className={styles.separator} />
        </li>

        {breadcrumbs.map((crumb, index) => {
          // The last item is ALWAYS plain text (either the current search location or the Listing Title)
          const isLast = index === breadcrumbs.length - 1;

          if (isLast) {
            return (
              <li key={`crumb-${index}`} className={styles.breadcrumbItem}>
                <span className={styles.breadcrumbCurrent}>{crumb.name}</span>
              </li>
            );
          }

          let crumbHref = "";
          if (crumb.type === "agency") {
            // Using standard next-intl route structure. Adjust if you have a specific route helper mapping.
            crumbHref = makeLocalizedHrefNoLocale(locale, "agency-profile", [
              crumb.url,
            ]);
          } else if (crumb.type === "user") {
            crumbHref = makeLocalizedHrefNoLocale(locale, "profiles", [
              crumb.url,
            ]);
          } else {
            const basePath = makeLocalizedHrefNoLocale(locale, "search", [
              crumb.url,
              TypeEnum.APARTMENT_HOUSE,
              PurposeEnum.FORSALE,
              "1",
            ]);

            // 🚀 Inject targets into the physical <a> tag href
            const params = new URLSearchParams();
            if (targetAgencyId) params.set("target_agency_id", targetAgencyId);
            if (targetUserId) params.set("target_user_id", targetUserId);

            const queryString = params.toString();
            crumbHref = queryString ? `${basePath}?${queryString}` : basePath;
          }

          return (
            <li key={`crumb-${index}`} className={styles.breadcrumbItem}>
              <a
                href={crumbHref}
                onClick={(e) => {
                  e.preventDefault();
                  handleClick(crumb);
                }}
                className={styles.breadcrumbLink}
              >
                {crumb.name}
              </a>
              <IoChevronForwardOutline className={styles.separator} />
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
